import { useCallback, useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isCriticalActivity, subscribeCriticalActivity } from "@/lib/critical-activity";

/**
 * Subtle, dismissible "new version available" notice for installed/TWA users.
 *
 * Rules:
 * - never reloads on its own; the refresh is always user-initiated
 * - stays hidden while a critical operation is running (Voice D.A.R. recording,
 *   SOS, an in-flight submission) and reappears once it finishes
 * - offline queues live in IndexedDB and survive the reload untouched
 */
export function UpdateAvailableBanner() {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [busyOp, setBusyOp] = useState(false);

  useEffect(() => {
    setBusyOp(isCriticalActivity());
    return subscribeCriticalActivity(() => setBusyOp(isCriticalActivity()));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    let disposed = false;
    let interval: number | undefined;

    void navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg || disposed) return;
      const check = () => {
        if (reg.waiting && navigator.serviceWorker.controller) setWaiting(reg.waiting);
      };
      check();
      reg.addEventListener("updatefound", () => {
        const installing = reg.installing;
        installing?.addEventListener("statechange", () => {
          if (installing.state === "installed" && navigator.serviceWorker.controller) {
            setWaiting(reg.waiting ?? installing);
            setDismissed(false);
          }
        });
      });
      // Look for a new deployment periodically; cheap and revalidation-friendly.
      interval = window.setInterval(() => void reg.update().catch(() => undefined), 30 * 60 * 1000);
    });

    return () => {
      disposed = true;
      if (interval) window.clearInterval(interval);
    };
  }, []);

  const refresh = useCallback(() => {
    const sw = waiting;
    if (!sw) {
      window.location.reload();
      return;
    }
    navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), {
      once: true,
    });
    sw.postMessage({ type: "SKIP_WAITING" });
  }, [waiting]);

  if (!waiting || dismissed || busyOp) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-3 bottom-20 z-40 mx-auto flex max-w-md items-center gap-3 rounded-xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur md:bottom-4"
      style={{ marginBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
        <RefreshCw className="h-4 w-4" />
      </div>
      <p className="min-w-0 flex-1 text-sm leading-tight text-foreground">
        New version available — refresh to update.
      </p>
      <Button size="sm" onClick={refresh}>Refresh</Button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss update notice"
        className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
